import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	cancelRegistration,
	createManualReservation,
	createOccurrences,
	deleteOccurrence,
	deleteOccurrencesBatch,
	exportReservations,
	getChats,
	getEventDetail,
	getMemberDetail,
	getOccurrenceDetail,
	getPublicHolidays,
	recordAttendance,
	sendChat,
	updateOccurrence,
	updateOccurrenceVisibility,
	updateRegistration,
	updateRegistrationStatus,
} from "../api/eventDetailApi";
import type {
	CreateOccurrencePayload,
	DeleteOccurrencesPayload,
	ManualReservationPayload,
	ReservationExportParams,
	UpdateAttendancePayload,
	UpdateOccurrencePayload,
	UpdateRegistrationPayload,
	UpdateRegistrationStatusPayload,
} from "../types";

export const EVENT_DETAIL_QUERY_KEY = "eventDetail";
export const PUBLIC_HOLIDAYS_QUERY_KEY = "publicHolidays";
export const OCCURRENCE_DETAIL_QUERY_KEY = "occurrenceDetail";

export const useEventDetail = (
	eventOccasionId: number | undefined,
	startDate?: string,
	endDate?: string,
) => {
	return useQuery({
		queryKey: [EVENT_DETAIL_QUERY_KEY, eventOccasionId, startDate, endDate],
		queryFn: () => getEventDetail(eventOccasionId || 0, startDate, endDate),
		enabled: !!eventOccasionId,
	});
};

export const usePublicHolidays = (enabled = true) => {
	return useQuery({
		queryKey: [PUBLIC_HOLIDAYS_QUERY_KEY],
		queryFn: getPublicHolidays,
		enabled,
		staleTime: 1000 * 60 * 60 * 24,
	});
};

export const useCreateManualReservation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: ManualReservationPayload) =>
			createManualReservation(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useCreateOccurrences = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: CreateOccurrencePayload) =>
			createOccurrences(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useDeleteOccurrence = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (occurrenceId: number) => deleteOccurrence(occurrenceId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useDeleteOccurrencesBatch = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: DeleteOccurrencesPayload) =>
			deleteOccurrencesBatch(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useExportReservations = () => {
	return useMutation({
		mutationFn: async (params: ReservationExportParams) => {
			const blob = await exportReservations(params);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `reservations_${params.eventOccasionId}_${new Date().toISOString().split("T")[0]}.csv`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
			return { success: true };
		},
	});
};

export const useOccurrenceDetail = (occurrenceId: number | undefined) => {
	return useQuery({
		queryKey: [OCCURRENCE_DETAIL_QUERY_KEY, occurrenceId],
		queryFn: () => getOccurrenceDetail(occurrenceId),
		enabled: !!occurrenceId,
	});
};

export const useCancelRegistration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (registrationId: number) => cancelRegistration(registrationId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: [OCCURRENCE_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useUpdateOccurrenceVisibility = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			occurrenceId,
			isDisplayed,
		}: {
			occurrenceId: number;
			isDisplayed: boolean;
		}) => updateOccurrenceVisibility(occurrenceId, isDisplayed),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: [OCCURRENCE_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const MEMBER_DETAIL_QUERY_KEY = "memberDetail";
export const CHATS_QUERY_KEY = "chats";

export const useMemberDetail = (memberId: number | undefined) => {
	return useQuery({
		queryKey: [MEMBER_DETAIL_QUERY_KEY, memberId],
		queryFn: () => getMemberDetail(memberId as number),
		enabled: !!memberId,
	});
};

export const useChats = (memberId: number | undefined, enabled = true) => {
	return useQuery({
		queryKey: [CHATS_QUERY_KEY, memberId],
		queryFn: () => getChats(memberId as number),
		enabled: !!memberId && enabled,
		refetchInterval: 5000,
	});
};

export const useSendChat = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			memberId,
			contents,
		}: {
			memberId: number;
			contents: string;
		}) => sendChat(memberId, contents),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: [CHATS_QUERY_KEY, variables.memberId],
			});
		},
	});
};

export const useUpdateRegistration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			registrationId,
			data,
		}: {
			registrationId: number;
			data: UpdateRegistrationPayload;
		}) => updateRegistration(registrationId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: [OCCURRENCE_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useUpdateOccurrence = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			occurrenceId,
			data,
		}: {
			occurrenceId: number;
			data: UpdateOccurrencePayload;
		}) => updateOccurrence(occurrenceId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: [OCCURRENCE_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useUpdateRegistrationStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			registrationId,
			payload,
		}: {
			registrationId: number;
			payload: UpdateRegistrationStatusPayload;
		}) => updateRegistrationStatus(registrationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: [OCCURRENCE_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};

export const useRecordAttendance = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			registrationId,
			payload,
		}: {
			registrationId: number;
			payload: UpdateAttendancePayload;
		}) => recordAttendance(registrationId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
			queryClient.invalidateQueries({
				queryKey: [OCCURRENCE_DETAIL_QUERY_KEY],
				refetchType: "active",
			});
		},
	});
};
